import { z } from "zod";

const OpaqueIdSchema = z.uuid();
const RequestIdSchema = z.string().regex(/^req_[0-9a-f-]{36}$/u);

export const WorkspaceExportScopeSchema = z.enum(["workspace", "child"]);
export const WorkspaceExportStatusSchema = z.enum([
  "queued",
  "running",
  "completed",
  "failed",
  "deleting",
  "deleted",
  "expired",
]);

export const WorkspaceExportSchema = z
  .object({
    id: OpaqueIdSchema,
    workspace_id: OpaqueIdSchema,
    scope: WorkspaceExportScopeSchema,
    child_id: OpaqueIdSchema.nullable(),
    include_media: z.boolean(),
    status: WorkspaceExportStatusSchema,
    archive_bytes: z.number().int().nonnegative().nullable(),
    table_counts: z.record(z.string().min(1).max(120), z.number().int().nonnegative()),
    media_entry_count: z.number().int().nonnegative(),
    media_bytes: z.number().int().nonnegative(),
    error_code: z.string().min(1).max(160).nullable(),
    version: z.number().int().positive(),
    requested_at: z.iso.datetime({ offset: true }),
    started_at: z.iso.datetime({ offset: true }).nullable(),
    completed_at: z.iso.datetime({ offset: true }).nullable(),
    expires_at: z.iso.datetime({ offset: true }),
    deleted_at: z.iso.datetime({ offset: true }).nullable(),
  })
  .strict()
  .refine(
    (value) =>
      (value.scope === "workspace" && value.child_id === null) ||
      (value.scope === "child" && value.child_id !== null),
    { message: "child_id must match the export scope" },
  );

export const CreateWorkspaceExportRequestSchema = z
  .object({
    scope: WorkspaceExportScopeSchema,
    child_id: OpaqueIdSchema.nullable(),
    include_media: z.boolean(),
    confirmation: z.literal("create_portable_export"),
  })
  .strict()
  .refine(
    (value) =>
      (value.scope === "workspace" && value.child_id === null) ||
      (value.scope === "child" && value.child_id !== null),
    { message: "child_id must match the export scope" },
  );

export const WorkspaceExportResponseSchema = z
  .object({
    export: WorkspaceExportSchema,
    idempotency_replayed: z.boolean(),
    request_id: RequestIdSchema,
  })
  .strict();

export const WorkspaceExportListResponseSchema = z
  .object({
    items: z.array(WorkspaceExportSchema).max(20),
    request_id: RequestIdSchema,
  })
  .strict();

export const DeleteWorkspaceExportRequestSchema = z
  .object({
    expected_version: z.number().int().positive(),
    confirmation: z.literal("delete_export_artifact"),
  })
  .strict();

export const WorkspaceExportWorkflowParamsSchema = z
  .object({
    action: z.enum(["build", "delete"]),
    export_id: OpaqueIdSchema,
    workspace_id: OpaqueIdSchema,
    requested_by_account_id: OpaqueIdSchema,
    scope: WorkspaceExportScopeSchema,
    child_id: OpaqueIdSchema.nullable(),
    include_media: z.boolean(),
    requested_at: z.number().int().nonnegative(),
  })
  .strict()
  .refine(
    (value) =>
      (value.scope === "workspace" && value.child_id === null) ||
      (value.scope === "child" && value.child_id !== null),
    { message: "child_id must match the export scope" },
  );

export type WorkspaceExport = z.infer<typeof WorkspaceExportSchema>;
export type CreateWorkspaceExportRequest = z.infer<typeof CreateWorkspaceExportRequestSchema>;
export type DeleteWorkspaceExportRequest = z.infer<typeof DeleteWorkspaceExportRequestSchema>;
export type WorkspaceExportWorkflowParams = z.infer<typeof WorkspaceExportWorkflowParamsSchema>;
